import React from 'react';
import { Button, Col, DatePicker, Form, Input, Row, Select } from 'antd';
import { Controller } from 'react-hook-form';

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { isValidPhoneNumber } from 'react-phone-number-input';
import { useTranslation } from 'react-i18next';

import { isEmpty as _isEmpty } from 'lodash-es';

import { isInvalidQuestion } from '../../../../utils/antdTableFormat';

import PhoneInputFormItem from '../../../PhoneInput';

const { Option } = Select;

export const DefaultJumpSection = ({ control }) => {
  const { t } = useTranslation();

  return (
    <div className="form-settings-response-jump-inputs">
      <Controller
        render={({ field, fieldState: { error } }) => (
          <PhoneInputFormItem {...field} error={error} />
        )}
        control={control}
        name="whatsappNumber"
        rules={{
          validate: (value) =>
            isValidPhoneNumber(`+${value}`) ? true : t('formSettings.enterValidNumber'),
        }}
      />
    </div>
  );
};

export const LogicJumpSection = ({
  fieldArray: { fields, append, remove, update },
  control,
  logicJumpData,
  form,
}) => {
  const { t } = useTranslation();

  return (
    <div className="form-settings-response-jump-inputs">
      {fields.map((field, index) => {
        // Index 0 is reserved for fallback phone number.
        if (index === 0) return;
        return (
          <Row
            key={field.id}
            className="form-settings-response-jump-inputs-strip"
            justify="center"
            gutter={[8, 8]}
          >
            <Col xs={24} sm={12}>
              <Controller
                control={control}
                name={`logicJumpData.${index}.questionId`}
                render={({ field, fieldState: { error } }) => (
                  <Form.Item
                    validateStatus={_isEmpty(error) ? 'success' : 'error'}
                    help={!_isEmpty(error) && error?.message}
                  >
                    <Select
                      {...field}
                      placeholder="Select question"
                      onChange={(e) => {
                        // Remove current inputs when question type changes
                        update(index, { answer: null });
                        field.onChange(e);
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      allowClear
                    >
                      {form.formQuestions
                        .filter(
                          (question) =>
                            !isInvalidQuestion[question.type] && !['FILE'].includes(question.type),
                        )
                        .map((question) => (
                          <Option value={question.questionId} key={question.questionId}>
                            {question.label}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                )}
                rules={{
                  required: t('formSettings.questionRequired'),
                }}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Controller
                control={control}
                name={`logicJumpData.${index}.answer`}
                render={({ field }) => {
                  const questionIds = form.formQuestions.map((question) => question.questionId);
                  const questionIndex = questionIds.indexOf(logicJumpData[index].questionId);
                  const question = form.formQuestions[questionIndex];

                  switch (question?.type) {
                    case 'RADIO':
                    case 'DROPDOWN': {
                      return (
                        <Select
                          {...field}
                          placeholder="Select answer"
                          disabled={logicJumpData[index].questionId === null}
                          showSearch
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          allowClear
                        >
                          {question.options.map((option) => (
                            <Option value={option.label} key={option.id}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      );
                    }
                    case 'CHECKBOX': {
                      return (
                        <Select
                          mode="multiple"
                          {...field}
                          defaultValue={[]}
                          placeholder="Select option"
                          id="form-settings-logic-checkbox-select"
                          value={logicJumpData[index].answer === null ? undefined : field.value}
                          disabled={logicJumpData[index].questionId === null}
                          showSearch
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          allowClear
                        >
                          {question.options.map((option) => (
                            <Option value={option.label} key={option.id}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      );
                    }
                    case 'RATING': {
                      const options = Array.from(
                        { length: question.ratingStyle === 'survey' ? 10 : 5 },
                        (_, i) => i + 1,
                      );
                      return (
                        <Select
                          {...field}
                          placeholder="Select rating"
                          disabled={logicJumpData[index].questionId === null}
                        >
                          {options.map((option) => (
                            <Option value={option} key={option}>
                              {option.toString()}
                            </Option>
                          ))}
                        </Select>
                      );
                    }
                    case 'DATE': {
                      return (
                        <DatePicker
                          {...field}
                          format="DD-MM-YYYY"
                          showTime={false}
                          showToday={false}
                          style={{ width: '100%' }}
                          disabled={logicJumpData[index].questionId === null}
                        />
                      );
                    }

                    default: {
                      return (
                        <Input
                          placeholder={t('formSettings.answerField')}
                          {...field}
                          disabled={logicJumpData[index].questionId === null}
                        />
                      );
                    }
                  }
                }}
              />
            </Col>
            <Col xs={20}>
              <Controller
                control={control}
                name={`logicJumpData.${index}.number`}
                render={({ field, fieldState: { error } }) => (
                  <PhoneInputFormItem {...field} error={error} />
                )}
                rules={{
                  validate: (value) =>
                    isValidPhoneNumber(`+${value}`) ? true : t('formSettings.enterValidNumber'),
                }}
              />
            </Col>
            <Col xs={4}>
              <Button icon={<DeleteOutlined />} onClick={() => remove(index)} danger></Button>
            </Col>
          </Row>
        );
      })}

      <Button
        icon={<PlusOutlined />}
        onClick={() => append({ number: null, questionId: null, answer: null })}
      >
        {t('formSettings.addCondition')}
      </Button>
      <Controller
        render={({ field, fieldState: { error } }) => (
          <Form.Item
            label={t('formSettings.fallbackNumber')}
            labelAlign="left"
            className="form-settings-logic-jump-fallback"
          >
            <PhoneInputFormItem {...field} error={error} />
          </Form.Item>
        )}
        control={control}
        name={`logicJumpData.${0}.number`}
        rules={{
          validate: (value) =>
            isValidPhoneNumber(`+${value}`) ? true : t('formSettings.enterValidNumber'),
        }}
      />
    </div>
  );
};

export const SequenceJumpSection = ({ fieldArray: { fields, append, remove }, control }) => {
  const { t } = useTranslation();

  return (
    <div className="form-settings-response-jump-inputs">
      {fields.map((field, index) => {
        return (
          <Row
            key={field.id}
            gutter={8}
            justify="center"
            className="form-settings-response-jump-inputs-strip"
            style={{
              backgroundColor: '#ffffff00',
              padding: '0',
            }}
          >
            <Col xs={18} sm={12}>
              <Controller
                control={control}
                name={`sequenceJumpData.${index}.number`}
                render={({ field, fieldState: { error } }) => (
                  <PhoneInputFormItem {...field} error={error} />
                )}
                rules={{
                  validate: (value) =>
                    isValidPhoneNumber(`+${value}`) ? true : t('formSettings.enterValidNumber'),
                }}
              />
            </Col>
            <Col xs={4} sm={2}>
              <Button icon={<DeleteOutlined />} onClick={() => remove(index)} danger></Button>
            </Col>
          </Row>
        );
      })}

      <Button icon={<PlusOutlined />} onClick={() => append({ number: null })}>
        {t('formSettings.addNumber')}
      </Button>
      {}
    </div>
  );
};
